import { useEffect } from 'react';

import { useErrorBoundary } from 'react-error-boundary';

import { Spinner } from '@pulse-web-ui/spinner';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import { useGetMVPProfile } from '@entities/agent-contract';

import { BoundaryErrorType, ProfileType } from '@shared/constants';
import { AgentStatus, AnalyticEvent, useStores } from '@shared/index';

import { AgentProfileActive } from './agent-profile-active';
import { AgentProfileConfirm } from './agent-profile-confirm';

export const AgentProfile = observer(() => {
  const { showBoundary } = useErrorBoundary();
  const {
    MainStore: {
      agentContractStore: { profile, agentProfileDraft, profileAddendumStatus },
      agentUserStore: { availableSign, userKind },
    },
  } = useStores();
  const isAgentMVP = userKind?.profileType === ProfileType.AGENT_MVP;

  const {
    res: mvpProfile,
    isFetching: isMVPProfileFetching,
    refetch: refetchMVPProfile,
    error: mvpProfileError,
  } = useGetMVPProfile();

  useEffect(() => {
    if (isAgentMVP) {
      refetchMVPProfile();
    }
  }, [isAgentMVP]);

  useEffect(() => {
    sendAnalyticEvent(AnalyticEvent.AGENT_PROFILE_OPEN, {
      isagent: availableSign?.visibilityDocuments ? 'n' : 'y',
    });
  }, [availableSign]);

  useEffect(() => {
    if (mvpProfileError) {
      showBoundary(BoundaryErrorType.SomethingWentWrong);
    }
  }, [mvpProfileError]);

  if (!profile || isMVPProfileFetching) {
    return <Spinner />;
  }

  const { status } = profile;

  const isAgentWithContract =
    status &&
    [
      AgentStatus.CONFIRM,
      AgentStatus.PENDING_SECURITY,
      AgentStatus.CONFIRM_SECURITY,
      AgentStatus.CONTRACT_DRAFT_FULL,
      AgentStatus.APPROVED,
    ].includes(status);

  if (status === AgentStatus.ACTIVE) {
    return <AgentProfileActive profile={profile} />;
  }

  const isDisplayEditButtons =
    !isAgentMVP && agentProfileDraft && !!Object.keys(agentProfileDraft).length;

  if (isAgentWithContract) {
    return (
      <AgentProfileConfirm
        profile={profile}
        mvpProfile={mvpProfile}
        profileAddendumStatus={profileAddendumStatus}
        isDisplayEditButtons={!!isDisplayEditButtons}
      />
    );
  }
});
